// The default Origination URI the console provisions for static-host platforms
// (Retell, Vapi, ElevenLabs, xAI) so inbound trunk creation needs no URI step:
// one per platform, on the platform's default transport, editable afterwards.
// "Already provisioned" is decided by platform host, not by exact string, so a
// customer who edits the default (other transport, auth) is left alone.

import type { ROriginationURIInternal } from 'v2/types/api/settings'
import {
  buildStaticSipUri,
  hasDefaultPlatformUris,
  matchesPlatformUri,
  SIP_PLATFORMS,
  type SipPlatformKey,
} from './platform-config'
import type { UnifiedApplicationFormValues } from './types'

const MAX_NAME_ATTEMPTS = 5

export interface DefaultUriPayload {
  name: string
  uri: string
}

/** Stored URIs are host-only strings; compare them case- and space-insensitively. */
export const normalizeStoredUri = (uri: string): string => uri.trim().toLowerCase()

export const defaultPlatformUriName = (key: SipPlatformKey): string =>
  `${SIP_PLATFORMS[key].defaultUriLabel ?? SIP_PLATFORMS[key].label}-URI`

/** The platform's default URI on its default transport, e.g. sip.vapi.ai;transport=udp */
export const buildDefaultPlatformUri = (
  key: SipPlatformKey | undefined,
  region?: string,
): DefaultUriPayload | undefined => {
  if (!key || !hasDefaultPlatformUris(key)) return undefined
  const uri = buildStaticSipUri(key, SIP_PLATFORMS[key].defaultTransport, region)
  return uri ? { name: defaultPlatformUriName(key), uri } : undefined
}

const byPreference = (a: ROriginationURIInternal, b: ROriginationURIInternal) =>
  Number(a.is_auth) - Number(b.is_auth) || a.id - b.id

/**
 * The account URI to auto-select for a platform: the untouched default if it is
 * there, else whatever URI the customer keeps for that platform (they may have
 * edited the default onto another transport). Prefers no auth, then the oldest.
 */
export const findDefaultPlatformUri = (
  key: SipPlatformKey | undefined,
  region: string | undefined,
  uris: ROriginationURIInternal[],
): ROriginationURIInternal | undefined => {
  const spec = buildDefaultPlatformUri(key, region)
  if (!key || !spec) return undefined
  const target = normalizeStoredUri(spec.uri)
  const exact = uris.filter((uri) => normalizeStoredUri(uri.uri) === target).sort(byPreference)[0]
  if (exact) return exact
  return uris.filter((uri) => matchesPlatformUri(uri.uri, key)).sort(byPreference)[0]
}

/**
 * Nothing to create when the account already has any URI for the platform.
 * The name steps to "Vapi-URI 2" when a customer URI of another host took it:
 * core enforces no uniqueness, trunk-central might.
 */
export const resolveMissingDefaultUri = (
  key: SipPlatformKey | undefined,
  region: string | undefined,
  existing: ROriginationURIInternal[],
): { createPayload?: DefaultUriPayload } => {
  const spec = buildDefaultPlatformUri(key, region)
  if (!key || !spec) return {}
  if (existing.some((uri) => matchesPlatformUri(uri.uri, key))) return {}
  const takenNames = new Set(existing.map((uri) => uri.name))
  let name = spec.name
  for (let attempt = 2; takenNames.has(name) && attempt <= MAX_NAME_ATTEMPTS; attempt++) {
    name = `${spec.name} ${attempt}`
  }
  if (takenNames.has(name)) return {}
  return { createPayload: { name, uri: spec.uri } }
}

/**
 * Core answers an account with ZERO Origination URIs with 400 "URIs not found"
 * instead of an empty list. For provisioning that is the most important case of
 * all (a fresh account), and for the dropdown it is just "nothing yet".
 */
export const isEmptyOriginationUriListError = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number; data?: unknown } } | undefined)
    ?.response
  if (!response || response.status !== 400) return false
  const body = response.data as
    | { errors?: { global_error?: unknown }; data?: { error?: unknown }; message?: unknown }
    | undefined
  const texts = [body?.errors?.global_error, body?.data?.error, body?.message]
  return texts.some((t) => typeof t === 'string' && /uris? not found/i.test(t))
}

export type UriListFetcher = () => Promise<{ data?: ROriginationURIInternal[] }>
export type UriCreator = (payload: DefaultUriPayload) => Promise<unknown>

export interface EnsureDefaultUriResult {
  created: boolean
  failed: boolean
}

/**
 * Idempotent provisioning: list, create the default if the platform has no URI
 * yet, and on a create error re-list once before calling it a failure (a race
 * with another tab or a uniqueness constraint means the customer already has
 * one). Never throws; the caller decides how to surface `failed`.
 */
export const ensureDefaultPlatformUri = async ({
  platform,
  region,
  listUris,
  createUri,
}: {
  platform: SipPlatformKey
  region?: string
  listUris: UriListFetcher
  createUri: UriCreator
}): Promise<EnsureDefaultUriResult> => {
  let existing: ROriginationURIInternal[]
  try {
    existing = (await listUris()).data ?? []
  } catch {
    return { created: false, failed: true }
  }
  const { createPayload } = resolveMissingDefaultUri(platform, region, existing)
  if (!createPayload) return { created: false, failed: false }
  try {
    await createUri(createPayload)
    return { created: true, failed: false }
  } catch {
    try {
      const fresh = (await listUris()).data ?? []
      const stillMissing = !!resolveMissingDefaultUri(platform, region, fresh).createPayload
      return { created: !stillMissing, failed: stillMissing }
    } catch {
      return { created: false, failed: true }
    }
  }
}

/**
 * The created record out of a create-URI response. Core forwards Zentrunk's raw
 * body, so accept the flat record, a `uri` / `data` wrapper, or nothing usable.
 */
export const extractCreatedUri = (body: unknown): ROriginationURIInternal | undefined => {
  const isRecord = (v: unknown): v is ROriginationURIInternal =>
    !!v &&
    typeof v === 'object' &&
    typeof (v as { id?: unknown }).id === 'number' &&
    typeof (v as { uri?: unknown }).uri === 'string'
  if (isRecord(body)) return body
  const wrapper = body as { uri?: unknown; data?: unknown } | undefined
  if (isRecord(wrapper?.uri)) return wrapper.uri
  if (isRecord(wrapper?.data)) return wrapper.data
  return undefined
}

/**
 * Zentrunk answers a create with `{ msg }` only, no record. Recover it from the
 * refetched list: same name and uri, newest id wins.
 */
export const findCreatedUri = (
  uris: ROriginationURIInternal[],
  sent: DefaultUriPayload,
): ROriginationURIInternal | undefined =>
  uris
    .filter(
      (uri) =>
        uri.name === sent.name && normalizeStoredUri(uri.uri) === normalizeStoredUri(sent.uri),
    )
    .sort((a, b) => b.id - a.id)[0]

/**
 * A URI just created from the modal becomes the Primary URI only when Primary
 * is still empty (never override a pick or an existing trunk's URI) and the URI
 * belongs to the selected platform. Custom and "no platform" accept anything.
 */
export const shouldSelectCreatedPrimaryUri = (
  values: UnifiedApplicationFormValues,
  uri: ROriginationURIInternal,
): boolean => {
  if (values.application_type !== 'sip' || values.trunk_direction !== 'inbound') return false
  if (typeof uri?.id !== 'number' || typeof uri.uri !== 'string') return false
  if ('primary_uri' in values && values.primary_uri) return false
  const platform = values.sip_platform as SipPlatformKey | undefined
  return !platform || matchesPlatformUri(uri.uri, platform)
}
